import { axiosInstance } from "@/api/api-client";
import { useToast } from "@/hooks/use-toast";
import { useCallback, useMemo, useRef, useState } from "react";

// Reuse your existing building blocks
import { getMediaFolders, getMediaList, storeMedia } from "@/hooks/media-api";
import type { MediaAsset, MediaFolder, MediaInput } from "@/types/media";


const MAX_BYTES = 25 * 1024 * 1024; // keep aligned with backend
const PAGE_SIZE = 40;

function putWithProgress(
    url: string,
    file: File,
    contentType: string,
    signal: AbortSignal,
    onProgress: (pct: number) => void
): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", contentType);
        const onAbort = () => {
            xhr.abort();
            reject(new DOMException("Aborted", "AbortError"));
        };
        signal.addEventListener("abort", onAbort, { once: true });
        xhr.upload.onprogress = (evt) => {
            if (evt.lengthComputable) onProgress((evt.loaded / evt.total) * 100);
        };
        xhr.onload = () => {
            signal.removeEventListener("abort", onAbort);
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`S3 responded ${xhr.status}: ${xhr.statusText}`));
        };
        xhr.onerror = () => {
            signal.removeEventListener("abort", onAbort);
            reject(new Error("Network error during upload"));
        };
        xhr.send(file);
    });
}

// -----------------------------
// useMediaLibrary — central, reusable state & actions
// -----------------------------
export function useMediaLibrary(initialParentId: string | null = null) {
    const { toast } = useToast();

    // folders
    const [folderTree, setFolderTree] = useState<MediaFolder[]>([]);
    const [currentFolder, setCurrentFolder] = useState<MediaFolder | null>(null);
    const [currentParentId, setCurrentParentId] = useState<string | null>(initialParentId);

    // assets
    const [mediaAssets, setMediaAssets] = useState<MediaAsset[]>([]);
    const [loadingAssets, setLoadingAssets] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [query, setQuery] = useState("");

    // selection
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const selectedItems = useMemo(() => mediaAssets.filter((a) => selectedIds.includes(a.id)), [mediaAssets, selectedIds]);

    // upload
    const [files, setFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [fileProgresses, setFileProgresses] = useState<Record<string, number>>({});
    const abortRefs = useRef<AbortController[]>([]);

    // Keep a single `file` / `progress` alias for backwards-compat if anything else reads them
    const file = files[0] ?? null;
    const progress = files.length === 0 ? 0 : Math.round(
        Object.values(fileProgresses).reduce((a, b) => a + b, 0) / Math.max(files.length, 1)
    );



    const fetchFolderTree = useCallback(async (folder: MediaFolder | null) => {
        try {
            const res = await getMediaFolders(folder ? folder.id : null);
            if (folder) {
                folder.children = res.data || [];
                setFolderTree((prev) => [...prev]);
            } else {
                setFolderTree(res.data || []);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    const fetchMediaAssets = useCallback(
        async (opts?: { reset?: boolean, parentId: string | null, query?: string | null }) => {
            const reset = !!opts?.reset;
            setLoadingAssets(true);
            try {
                const res = await getMediaList(opts!.parentId, opts?.query);
                const list = (res?.data as MediaAsset[]) || [];
                setMediaAssets((prev) => (reset ? list : [...prev, ...list]));
                setHasMore(list.length === PAGE_SIZE);
            } catch (e) {
                console.error(e);
                toast({ title: "Error", description: "Could not fetch media assets.", variant: "destructive" });
            } finally {
                setLoadingAssets(false);
            }
        },
        [currentParentId, page, query, toast]
    );
    const resetList = useCallback(async (parentId: string | null) => {
        setMediaAssets([]);
        setPage(0);
        setHasMore(false);
        console.log(currentParentId, 'current parent id');
        await fetchMediaAssets({ reset: true, parentId, query });
    }, [currentParentId]);

    const validateAndSet = (incoming: File | File[]) => {
        const list = Array.isArray(incoming) ? incoming : [incoming];
        const valid: File[] = [];
        for (const f of list) {
            if (f.size > MAX_BYTES) {
                toast({ title: "File too large", description: `${f.name} exceeds 25MB.`, variant: "destructive" });
            } else {
                valid.push(f);
            }
        }
        if (valid.length > 0) setFiles((prev) => [...prev, ...valid]);
    };

    const removeFile = (name: string) => {
        setFiles((prev) => prev.filter((f) => f.name !== name));
        setFileProgresses((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    const doUpload = async () => {
        if (files.length === 0) {
            toast({ title: "No files selected", description: "Pick at least one file to upload." });
            return;
        }
        setUploading(true);
        setFileProgresses({});
        abortRefs.current = [];
        let successCount = 0;
        try {
            await Promise.all(
                files.map(async (f) => {
                    const controller = new AbortController();
                    abortRefs.current.push(controller);
                    try {
                        const presignRes = await axiosInstance.get(
                            `/media/s3/presign?filename=${encodeURIComponent(f.name)}&contentType=${encodeURIComponent(f.type)}`
                        );
                        const { url, key, publicUrl } = presignRes.data as { url: string; key: string; publicUrl: string; expiresIn: number };
                        await putWithProgress(url, f, f.type, controller.signal, (pct) => {
                            setFileProgresses((prev) => ({ ...prev, [f.name]: pct }));
                        });
                        const payload: MediaInput = {
                            fileName: f.name,
                            fileType: f.type,
                            fileSize: f.size,
                            s3Key: key,
                            s3Url: publicUrl,
                            parentId: currentParentId,
                        };
                        const res = await storeMedia(payload);
                        if (res.success) {
                            successCount++;
                        } else {
                            toast({ title: `Failed: ${f.name}`, description: res.message || "Unknown error", variant: "destructive" });
                        }
                    } catch (e: any) {
                        if (e?.name !== "AbortError") {
                            toast({ title: `Upload failed: ${f.name}`, description: "Could not upload file.", variant: "destructive" });
                        }
                    }
                })
            );
            if (successCount > 0) {
                toast({ title: "Uploaded", description: `${successCount} file${successCount > 1 ? "s" : ""} uploaded successfully.` });
                setFiles([]);
                setFileProgresses({});
                resetList(currentParentId);
            }
        } finally {
            setUploading(false);
            abortRefs.current = [];
        }
    };

    const cancelUpload = () => {
        abortRefs.current.forEach((c) => c.abort());
    };

    const toggleSelect = (id: string, multi = true) => {
        setSelectedIds((prev) => {
            if (multi) return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
            return prev.includes(id) ? [] : [id];
        });
    };

    return {
        // state
        folderTree, currentFolder, currentParentId,
        mediaAssets, loadingAssets, page, hasMore, query,
        selectedIds, selectedItems,
        file, files, uploading, progress, fileProgresses,

        // actions
        setQuery, setPage, setCurrentFolder, setCurrentParentId, setSelectedIds,
        resetList,
        fetchFolderTree, fetchMediaAssets,
        validateAndSet, removeFile, doUpload, cancelUpload,
        toggleSelect,
    } as const;
}
